import { fetchClient } from "@/api/api";
import type {
	SenderPriorityUsage,
	batchReloginInput,
	batchReloginOutput,
	createSenderInput,
	createSenderOutput,
	deleteManySendersInput,
	deleteManySendersOutput,
	deleteSenderInput,
	deleteSenderOutput,
	getAllSendersInput,
	getAllSendersOutput,
	getSenderInput,
	getSenderOutput,
	senderHistoryInput,
	senderHistoryOutput,
	senderReloginInput,
	senderReloginOutput,
	sendersFacetsInput,
	sendersFacetsOutput,
	stopResumeManyInput,
	stopResumeManyOutput,
	stopResumeSenderInput,
	stopResumeSenderOutput,
	stopResumeSenderPath,
	updateSenderInput,
	updateSenderOutput,
	updateSenderPath,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { ADMIN_COMFORT_TRADE_SENDERS_SERVICE_KEYS } from "@/services/admin_comfort_trade_senders";
import { ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS } from "@/services/admin_fc_internal_senders";
import { createFacetSource } from "@/services/facets";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SENDERS_SERVICE_KEYS } from "./keys";

/**
 * Sender mutations are shared with the admin pages, which read the same senders through
 * their own queries, so those have to be refreshed too.
 */
const invalidateSenderLists = () => {
	invalidateQueries(SENDERS_SERVICE_KEYS.useGetAllSenders());
	invalidateQueries(
		ADMIN_COMFORT_TRADE_SENDERS_SERVICE_KEYS.useGetAdminComfortTradeSenders(),
	);
	invalidateQueries(
		ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSenders(),
	);
	invalidateQueries(
		ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSender(),
	);
};

export const useGetAllSenders: CustomQueryHook<
	getAllSendersOutput,
	getAllSendersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDERS_SERVICE_KEYS.useGetAllSenders(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/senders/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}
			return data;
		},
	});
};

export const useCreateSender: CustomMutationHook<
	createSenderInput,
	createSenderOutput
> = () => {
	const { toastApiError } = useApiError();
	return useMutation<createSenderOutput, ApiError, createSenderInput>({
		mutationKey: SENDERS_SERVICE_KEYS.useCreateSender(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/senders/", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},

		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetSender: CustomQueryHook<getSenderOutput, getSenderInput> = (
	options,
) => {
	return useQuery({
		...options,
		queryKey: SENDERS_SERVICE_KEYS.useGetSender(),
		enabled: !!options.fc_sender_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/senders/{fc_sender_id}",
				{
					params: {
						path: { fc_sender_id: options.fc_sender_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateSender: CustomMutationHook<
	{ path: updateSenderPath; body: updateSenderInput },
	updateSenderOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDERS_SERVICE_KEYS.useUpdateSender(),
		mutationFn: async ({ path, body }) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/senders/{fc_sender_id}",
				{
					params: { path },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},

		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteSender: CustomMutationHook<
	deleteSenderInput,
	deleteSenderOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<deleteSenderOutput, ApiError, deleteSenderInput>({
		mutationKey: SENDERS_SERVICE_KEYS.useDeleteSender(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/senders/{fc_sender_id}",
				{
					params: {
						path: { fc_sender_id: payload.fc_sender_id },
					},
					throwOnError: true,
				},
			);

			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useStopResumeSender: CustomMutationHook<
	{ path: stopResumeSenderPath; body: stopResumeSenderInput },
	stopResumeSenderOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDERS_SERVICE_KEYS.useStopResumeSender(),
		mutationFn: async ({ path, body }) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/senders/{fc_sender_id}",
				{
					params: { path },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useSenderHistory: CustomQueryHook<
	senderHistoryOutput,
	senderHistoryInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDERS_SERVICE_KEYS.useSenderHistory(payload),
		enabled: !!payload.fc_sender_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/senders/{fc_sender_id}/history/",
				{
					params: { path: { fc_sender_id: payload.fc_sender_id } },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useSenderRelogin: CustomMutationHook<
	senderReloginInput,
	senderReloginOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<senderReloginOutput, ApiError, senderReloginInput>({
		mutationKey: SENDERS_SERVICE_KEYS.useSenderRelogin(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/senders/{fc_sender_id}/relogin/",
				{
					params: { path: { fc_sender_id: payload.fc_sender_id } },
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useSendersFacets: CustomQueryHook<
	sendersFacetsOutput,
	sendersFacetsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDERS_SERVICE_KEYS.useSendersFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/senders/facets/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useStopResumeMany: CustomMutationHook<
	stopResumeManyInput,
	stopResumeManyOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<stopResumeManyOutput, ApiError, stopResumeManyInput>({
		mutationKey: SENDERS_SERVICE_KEYS.useStopResumeMany(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/senders/stop-resume/",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteManySenders: CustomMutationHook<
	deleteManySendersInput,
	deleteManySendersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<deleteManySendersOutput, ApiError, deleteManySendersInput>(
		{
			mutationKey: SENDERS_SERVICE_KEYS.useDeleteManySenders(),
			mutationFn: async (body) => {
				const { data, error } = await fetchClient.POST(
					"/v1/fc/senders/delete-many/",
					{
						body,
						throwOnError: true,
					},
				);
				if (error) {
					throw error;
				}
				return data;
			},
			onSuccess: () => {
				invalidateSenderLists();
			},
			onError: (error) => {
				toastApiError(error);
			},
		},
	);
};

export const useBatchUpdateSenderPriority = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		void,
		ApiError,
		{ senderIds: string[]; priorityUsage: SenderPriorityUsage | null }
	>({
		mutationKey: SENDERS_SERVICE_KEYS.useBatchUpdateSenderPriority(),
		mutationFn: async ({ senderIds, priorityUsage }) => {
			const { error } = await fetchClient.POST("/v1/fc/senders/update-many/", {
				body: { sender_ids: senderIds, priority_usage: priorityUsage },
				throwOnError: true,
			});
			if (error) throw error;
		},
		onSuccess: invalidateSenderLists,
		onError: toastApiError,
	});
};

export const useBatchRelogin: CustomMutationHook<
	batchReloginInput,
	batchReloginOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDERS_SERVICE_KEYS.useBatchRelogin(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/senders/relogin/",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateSenderLists();
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const SENDERS_FACET_SOURCE = createFacetSource<sendersFacetsOutput>({
	path: "/v1/fc/senders/facets/",
	queryKey: (query) => SENDERS_SERVICE_KEYS.useSendersFacets(query as any),
});
